import React, { useMemo, useState } from 'react'
import './Bracket.css'
import { buildStaging } from '../data/staging'

const ZONES = [
  { id: 'local', label: 'Local', timeZone: undefined },
  { id: 'pt', label: 'PT', timeZone: 'America/Los_Angeles' },
  { id: 'et', label: 'ET', timeZone: 'America/New_York' },
  { id: 'cest', label: 'CEST', timeZone: 'Europe/Berlin' },
]

const MatchCard = ({ match, fmt, focus, setFocus }) => (
  <div
    className={`br-match${focus === match.id ? ' is-focus' : ''}${match.bestOf === 5 ? ' is-bo5' : ''}`}
    onMouseEnter={() => setFocus(match.id)}
    onMouseLeave={() => setFocus(null)}
  >
    <div className="br-match-head">
      <span className="br-match-num">M{match.number}</span>
    </div>
    <div className="br-slot">{match.a}</div>
    <div className="br-slot">{match.b}</div>
    <div className="br-match-time">
      Day {match.day + 1} · {fmt.time(match.start)}
    </div>
  </div>
)

const BracketColumns = ({ title, columns, fmt, focus, setFocus }) => (
  <div className="br-tree">
    <p className="br-tree-title">{title}</p>
    <div className="br-cols">
      {columns.map((col) => (
        <div className="br-col" key={col[0].id}>
          <p className="br-col-head">
            {col[0].name}
            <span className={col[0].bestOf === 5 ? 'is-bo5' : 'is-bo3'}>Bo{col[0].bestOf}</span>
          </p>
          <div className="br-col-body">
            {col.map((m) => (
              <MatchCard key={m.id} match={m} fmt={fmt} focus={focus} setFocus={setFocus} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
)

const Bracket = () => {
  const [zone, setZone] = useState('local')
  const [focus, setFocus] = useState(null)

  const bracket = useMemo(() => buildStaging(), [])

  const fmt = useMemo(() => {
    const { timeZone } = ZONES.find((z) => z.id === zone)
    const time = new Intl.DateTimeFormat(undefined, { timeZone, hour: 'numeric', minute: '2-digit' })
    const timeTz = new Intl.DateTimeFormat(undefined, {
      timeZone, weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    })
    return { time: (d) => time.format(d), full: (d) => timeTz.format(d) }
  }, [zone])

  return (
    <div className="br">
      <p className="br-disclaimer">Proposed seeding and schedule — not final; match times may shift.</p>

      <div className="br-controls">
        <div className="br-control">
          <span className="br-control-label">Times in</span>
          <div className="br-seg" role="radiogroup" aria-label="Time zone">
            {ZONES.map((z) => (
              <button
                key={z.id}
                type="button"
                role="radio"
                aria-checked={zone === z.id}
                className={zone === z.id ? 'is-active' : ''}
                onClick={() => setZone(z.id)}
              >
                {z.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="br-scroll">
        <BracketColumns title="Upper Bracket" columns={bracket.upper} fmt={fmt} focus={focus} setFocus={setFocus} />
        <BracketColumns title="Lower Bracket" columns={bracket.lower} fmt={fmt} focus={focus} setFocus={setFocus} />
        <div className="br-tree">
          <p className="br-tree-title">Grand Final</p>
          <div className="br-cols">
            <div className="br-col">
              <div className="br-col-body">
                <MatchCard match={bracket.final} fmt={fmt} focus={focus} setFocus={setFocus} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="br-days">
        {bracket.days.map((day, d) => (
          <div className="br-day" key={day.day}>
            <div className="br-day-head">
              <p className="br-day-kicker">PLAY DAY {d + 1} · OCT {day.day}</p>
              <p className="br-day-range">
                {fmt.full(new Date(day.start))} → {fmt.time(day.end)}
              </p>
            </div>
            <ol className="br-day-list">
              {day.matches.map((m) => (
                <li
                  key={m.id}
                  className={`${focus === m.id ? 'is-focus' : ''}${m.bestOf === 5 ? ' is-bo5' : ''}`}
                  onMouseEnter={() => setFocus(m.id)}
                  onMouseLeave={() => setFocus(null)}
                >
                  <span className="br-row-time">{fmt.time(m.start)}</span>
                  <span className="br-row-body">
                    <span className="br-row-title">
                      M{m.number} · {m.name} <em>Bo{m.bestOf}</em>
                    </span>
                    <span className="br-row-teams">{m.a} vs {m.b}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>

    </div>
  )
}

export default Bracket
